import React, { useEffect, useState, useCallback, useRef } from 'react';
import Blog from './Blog';
import { host } from '../script/variables';
import Nav from '../common/Nav';
import { Link } from 'react-router-dom';
import './style/bloghome.css';
import BottomNav from '../BottomNav';

const PAGE_SIZE = 10;

const BlogsHome = () => {
  const [blogs,     setBlogs]     = useState([]);
  const [pno,       setPno]       = useState(1);
  const [hasMore,   setHasMore]   = useState(true);
  const [isLogin,   setIsLogin]   = useState(false);
  const [searchInp, setSearchInp] = useState('');
  const [loading,   setLoading]   = useState(false);   // initial / search load
  const [loadingMore, setLoadingMore] = useState(false); // infinite scroll load
  const [error,     setError]     = useState('');

  /* keeps the current active search term separate from the input box */
  const activeSearch = useRef('');

  /* ── auth ── */
  const checkLogin = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${host}/user/vlogin`, {
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      setIsLogin(res.ok);
    } catch { /* silent */ }
  };

  /* ── fetch ── */
  const fetchBlogs = useCallback(async (page = 1, search = activeSearch.current) => {
    if (page === 1) setLoading(true);
    else            setLoadingMore(true);
    setError('');

    try {
      const res = await fetch(`${host}/blog/fetchwithinp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searchText: search, pageNo: page }),
      });

      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();

      setBlogs(prev => page === 1 ? data : [...prev, ...data]);
      setPno(page + 1);
      setHasMore(data.length >= PAGE_SIZE);
    } catch {
      setError('Could not load posts. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { checkLogin(); fetchBlogs(1); }, []);

  /* ── search / reset ── */
  const handleSearch = () => {
    activeSearch.current = searchInp;
    setBlogs([]);
    setPno(1);
    setHasMore(true);
    fetchBlogs(1, searchInp);
  };

  const handleReset = () => {
    activeSearch.current = '';
    setSearchInp('');
    setBlogs([]);
    setPno(1);
    setHasMore(true);
    fetchBlogs(1, '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  /* ── infinite scroll sentinel ── */
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchBlogs(pno);
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, pno, fetchBlogs]);

  /* ── skeleton cards ── */
  const Skeleton = () => (
    <div className="bh-skeleton-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bh-skeleton-card">
          <div className="bh-sk bh-sk-img" />
          <div className="bh-sk-body">
            <div className="bh-sk bh-sk-tag" />
            <div className="bh-sk bh-sk-title" />
            <div className="bh-sk bh-sk-title bh-sk-short" />
            <div className="bh-sk bh-sk-meta" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Nav isLogin={isLogin} setIsLogin={setIsLogin} vegMode={false} />

      <div className="bh-shell">

        {/* ── Hero search bar ── */}
        <header className="bh-hero">
          <h1 className="bh-hero-title">Restro<span>Pilots</span> Blog</h1>
          <p className="bh-hero-sub">Stories, tips and insights from the restaurant world</p>

          <div className="bh-search-row">
            <div className="bh-search-box">
              <svg className="bh-search-icon" viewBox="0 0 20 20" fill="none">
                <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.7" />
                <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <input
                className="bh-search-input"
                type="text"
                value={searchInp}
                onChange={e => setSearchInp(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search posts…"
              />
              {searchInp && (
                <button className="bh-search-clear" onClick={handleReset} aria-label="Clear">✕</button>
              )}
            </div>
            <button className="bh-search-btn" onClick={handleSearch} disabled={loading}>
              Search
            </button>
          </div>

          {activeSearch.current && (
            <div className="bh-active-search">
              Results for <strong>"{activeSearch.current}"</strong>
              <button className="bh-clear-tag" onClick={handleReset}>✕ Clear</button>
            </div>
          )}
        </header>

        {/* ── Write CTA ── */}
        <div className="bh-write-row">
          <span className="bh-write-label">Have something to share?</span>
          <Link to="/write" className="bh-write-btn">
            <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
              <path d="M14.5 2.5a2.121 2.121 0 013 3L7 16l-4 1 1-4L14.5 2.5z"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Write a post
          </Link>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="bh-error">
            <span>{error}</span>
            <button onClick={() => fetchBlogs(pno)}>Retry</button>
          </div>
        )}

        {/* ── Content ── */}
        {loading ? (
          <Skeleton />
        ) : blogs.length === 0 ? (
          <div className="bh-empty">
            <div className="bh-empty-icon">✍️</div>
            <p className="bh-empty-title">No posts found</p>
            <p className="bh-empty-sub">
              {activeSearch.current
                ? `Nothing matched "${activeSearch.current}" — try a different term.`
                : 'No blog posts yet. Be the first to write one!'}
            </p>
            {activeSearch.current && (
              <button className="bh-empty-reset" onClick={handleReset}>Clear search</button>
            )}
          </div>
        ) : (
          <>
            <div className="bh-grid">
              {blogs.map((blog, i) => (
                <Blog key={blog._id ?? i} blog={blog} />
              ))}
            </div>

            {/* IntersectionObserver sentinel */}
            <div ref={sentinelRef} className="bh-sentinel" />

            {loadingMore && (
              <div className="bh-load-more">
                <span className="bh-spinner" />
                <span>Loading more posts…</span>
              </div>
            )}

            {!hasMore && !loadingMore && (
              <div className="bh-end-msg">
                You've read it all 🎉
              </div>
            )}
          </>
        )}
        <BottomNav/>
      </div>
    </>
  );
};

export default BlogsHome;